import { getFeatureSetting } from "../../feature-settings";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import { makeText } from "../builder-utils";
import {
    ArrayPropertyStyle,
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import {
    makeCaptionStringPropertyDescriptor,
    makeTextPropertyDescriptorWithSpecialValue,
} from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { getArrayProperty, getWebhookProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    PropertyKind,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { BaseActionHandler } from "./base";
import { asMaybeString } from "../../computation-model/data";
import { TriggerAppWebhookActionBody, WebhookValues } from "../../firebase-function-types";
import { makeWebhookValue } from "../../components/component-helpers";

interface NameAndValue {
    readonly name: PropertyDescription;
    readonly value: PropertyDescription;
}

interface WebhookActionDescription extends ActionDescription {
    readonly webhook: PropertyDescription;
    readonly namesAndValues: PropertyDescription;
}

export class WebhookActionHandler extends BaseActionHandler<WebhookActionDescription> {
    public readonly kind = ActionKind.Webhook;
    public readonly iconName = "01-47-flash";

    public getDescriptor(
        _desc: WebhookActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Trigger Webhook",
            needsScreenContext: true,
            isLegacy: !getFeatureSetting("webhookAction"),
            properties: [
                {
                    kind: PropertyKind.Webhook,
                    label: "Webhook",
                    property: { name: "webhook" },
                    section: PropertySection.Data,
                },
                {
                    kind: PropertyKind.Array,
                    label: "Values",
                    property: { name: "namesAndValues" },
                    section: PropertySection.Data,
                    properties: [
                        makeCaptionStringPropertyDescriptor("Value", true, mutatingScreenKind, undefined, "name"),
                        makeTextPropertyDescriptorWithSpecialValue(
                            "value",
                            "Value",
                            "Enter value",
                            true,
                            mutatingScreenKind,
                            {
                                columnFirst: true,
                                isDefaultCaption: true,
                            }
                        ),
                    ],
                    allowEmpty: true,
                    allowReorder: false,
                    addItemLabel: "Add Value",
                    style: ArrayPropertyStyle.KeyValue,
                },
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: WebhookActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Webhook action");
        }

        const webhookID = getWebhookProperty(desc.webhook);
        if (webhookID === undefined) return undefined;

        const namesAndValues: SerializedComponent[] = [];

        const arr = getArrayProperty<NameAndValue>(desc.namesAndValues) ?? [];
        for (const nav of arr) {
            const name = makeText(builder, nav.name, tables.input, true, false, false, false, screenContext);
            const value = makeText(builder, nav.value, tables.input, true, false, true, false, screenContext);
            if (name === undefined || value === undefined) continue;
            namesAndValues.push(name, value);
        }

        return b.action("TriggerWebhook", {
            webhookID: b.constant(webhookID),
            namesAndValues: b.list(namesAndValues),
            onSuccess,
        });
    }

    public inflate(ib: WireInflationBackend, desc: WebhookActionDescription): WireActionHydrator | undefined {
        const { appID, appFacilities } = ib;

        const webhookID = getWebhookProperty(desc.webhook);
        if (webhookID === undefined) return undefined;

        const arr = getArrayProperty<NameAndValue>(desc.namesAndValues) ?? [];
        const getters = arr.map(nav => {
            const [nameGetter] = ib.getValueGetterForProperty(nav.name, false);
            const [valueGetter] = ib.getValueGetterForProperty(nav.value, false);
            return { nameGetter, valueGetter };
        });

        return vp => {
            const params: WebhookValues = {};

            for (const g of getters) {
                const name = asMaybeString(g.nameGetter(vp));
                if (name === undefined) return undefined;

                const value = makeWebhookValue(g.valueGetter(vp));
                if (value === undefined) return undefined;

                params[name] = value;
            }

            return async () => {
                const body: TriggerAppWebhookActionBody = {
                    appID,
                    webhookID,
                    params,
                };

                const result = await appFacilities.callAuthIfAvailableCloudFunction(
                    "triggerAppWebhookAction",
                    body,
                    {}
                );

                return result?.ok === true;
            };
        };
    }
}
